<script setup lang="ts">
import { Size } from '@/types/common.types';
import { CSSProperties, ref, watch } from 'vue';
import { IconType } from '@/types/icon.types';
import OneButton from '../button/OneButton.vue';

interface Props {
  isExpanded?: boolean;
  border?: boolean;
  size?: Size;
  padding?: CSSProperties['padding'];
  icon?: IconType;
  useStyle?: boolean;
  showToggle?: boolean;
  showTitle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isExpanded: true,
  border: false,
  size: 'large',
  padding: undefined,
  icon: 'icon-arrow-down',
  showToggle: true,
  showTitle: true,
});

const emit = defineEmits<{
  'update:isExpanded': [value: boolean];
}>();

defineSlots<{
  title: void;
  default: void;
}>();

const internalIsExpanded = ref(props.isExpanded ?? false);
const onClickExpand = () => {
  internalIsExpanded.value = !internalIsExpanded.value;
  emit('update:isExpanded', internalIsExpanded.value);
};

watch(
  () => props.isExpanded,
  (value) => {
    internalIsExpanded.value = value ?? false;
  },
);
</script>

<template>
  <div
    class="toggle-box"
    :class="{ expand: internalIsExpanded, border: props.border, [classes.details]: props.useStyle }"
  >
    <header
      v-if="props.showTitle"
      class="toggle-box__title"
    >
      <one-button
        v-if="props.showToggle"
        type="icon"
        :size="props.size"
        :left-icon="props.icon"
        @click="onClickExpand"
      />
      <slot name="title" />
    </header>
    <article
      v-show="internalIsExpanded"
      class="toggle-box__content"
    >
      <slot />
    </article>
  </div>
</template>

<style module="classes" lang="scss">
.details {
  border: 1px solid var(--color-gray-05-05);
  border-radius: 2px;

  > header {
    height: 32px;
    padding: 0 12px;
    border-bottom: 1px solid var(--color-gray-05-05);
    background-color: var(--color-gray-07-02);
    color: var(--main-font-color);

    @include typography('sub-title-3');
  }
}
</style>

<style scoped lang="scss">
.toggle-box {
  &__title {
    display: flex;
    gap: 4px;
    align-items: center;
    padding: v-bind('props.padding');
    border-bottom: 1px solid var(--color-gray-05-05);
    :deep(.one-button__left-icon) {
      transition: $animate-base;
    }
  }
  &.expand > &__title :deep(.one-button__left-icon) {
    transform: rotate(-180deg);
  }
  &.border {
    border: 1px solid var(--color-gray-05-05);
    border-bottom-color: transparent;
    border-radius: 2px;

    &.expand {
      border-bottom-color: var(--color-gray-05-05);
    }
  }
}
</style>
